import { useMemo, type ReactNode } from 'react';
import { MantineProvider } from '@mantine/core';
import type { Store } from 'effector';
import { useUnit } from 'effector-react';
import type { TenantBranding } from '@buildex/module-contract';
import { buildTenantTheme } from './build-tenant-theme';
import { BrandingStoreContext } from './branding-context';

interface TenantThemeProviderProps {
  /** Effector-стор брендинга (из core: $branding) */
  branding: Store<TenantBranding>;
  children: ReactNode;
}

/**
 * Подписан на стор через useUnit → смена объекта брендинга (загрузка конфига,
 * preview из админки) применяется мгновенно, без пересборки (§8).
 *
 * Схема light/dark форсируется из брендинга (forceColorScheme), чтобы
 * сохранённое в localStorage или системное значение не переопределяло
 * настройку тенанта. 'auto' — отдаём выбор системе.
 */
export function TenantThemeProvider({ branding, children }: TenantThemeProviderProps) {
  const current = useUnit(branding);
  const theme = useMemo(() => buildTenantTheme(current), [current]);
  const scheme = current.colorScheme ?? 'light';

  return (
    <BrandingStoreContext.Provider value={branding}>
      <MantineProvider
        theme={theme}
        defaultColorScheme={scheme}
        forceColorScheme={scheme === 'auto' ? undefined : scheme}
      >
        {children}
      </MantineProvider>
    </BrandingStoreContext.Provider>
  );
}
